// @ts-check
import {
  EDGE_TYPE,
  NODE_TYPE,
  addressNodeId,
  newGenericEdge,
  newGenericNode,
  newGroupEdge,
  newQueryId,
  tokenFlowEdgeId,
} from './reactFlowUtils';

import { TOP_WALLETS_PER_QUERY } from '../static/tracerConsts';

import { extractAddress } from '../../../utils/helperUtils';

const nodeYSeparation = 100;
const nodeXSeparation = 350;

/**
 * Adds a set of nodes to a group. This is triggered post group modal form submission.
 * @param {Object} focusNodePrepParams
 * @param {Map<string,string>} focusNodePrepParams.addressMap - The nodeIds to add to the group
 * @param {String | import('blockscope/static/chainData').enrichedAddress} focusNodePrepParams.filterAddress - the address being searched
 * @param {String} focusNodePrepParams.chain - the chain of the address being searched
 * @param {(import('./tracerStore').AddressNode | import('./tracerStore').GroupNode)[]} focusNodePrepParams.nodes - list of nods in tracer state
 * @param {(import('./tracerStore').NodeTransactionSearchParams | import('./tracerStore').NodeTokenFlowSearchParams)[]} focusNodePrepParams.searchParams
 * @returns {import('./tracerStore').AddressNode | import('./tracerStore').GroupNode}
 */
export const prepareFocusNode = ({
  addressMap,
  filterAddress,
  chain,
  nodes,
  searchParams,
}) => {
  // check if the search node is already in the graph
  if (addressMap.has(extractAddress(filterAddress)) === false) {
    // if not, create a new node and add it to the graph
    const focusNode = newGenericNode({
      address: filterAddress,
      searchParams,
      chain,
    });

    nodes.push(focusNode);
    addressMap.set(extractAddress(filterAddress), focusNode.id);

    return focusNode;
  } else {
    // if it is, update the old node with the new search params
    /** @type {import('./tracerStore').AddressNode | import('./tracerStore').GroupNode} */
    const focusNode = {
      ...nodes.find(
        (node) => node.id === addressMap.get(extractAddress(filterAddress))
      ),
    };

    if (focusNode.type === 'addressGroup') {
      // if group node, update the inner node with the new search params
      /** @type {import('./tracerStore').AddressNode } */
      const focusMemberNode = focusNode.data.members.find(
        (node) =>
          node.id ===
          addressNodeId({ chain, address: extractAddress(filterAddress) })
      );
      focusMemberNode.data.searchParams.push(...searchParams);
    } else if (focusNode.type === 'addressNode') {
      focusNode.data.searchParams.push(...searchParams);
    }
    return focusNode;
  }
};

/**
 * Adds a set of nodes to a group. This is triggered post group modal form submission.
 * @param {Object} nodeComputeParams
 * @param {Object} nodeComputeParams.focusNode
 * @param {Array} nodeComputeParams.nodes - list of nodes in tracer state
 * @param {Map<string,string>} nodeComputeParams.addressMap - The nodeIds to add to the group
 * @param {Object[]} nodeComputeParams.rawQueryData - the chain of the address being searched
 * @param {{address: string, chain: string}[]} nodeComputeParams.interestedAddresses - list of interested addresses
 * @param {import('stateManagement/queries/services/tracer/zod').TokenFlowRow[]} nodeComputeParams.results - the address being searched
 * @param {import('stateManagement/queries/services/tracer/zod').TokenFlowParams} nodeComputeParams.params
 */
export const computeNodeStateWithNewTokenFlow = ({
  focusNode,
  nodes,
  rawQueryData,
  interestedAddresses,
  addressMap,
  results,
  params,
}) => {
  let leftYPointer = 0;
  let rightYPointer = 0;
  for (let i = 0; i < results.length; i++) {
    const { flow, walletAddress } = results[i];
    const isInflow = flow.displayText.charAt(0) !== '-';

    // merge results and search params into queryData
    const record = {
      amount: flow,
      ...params,
    };
    if (isInflow) {
      record.sourceAddress = walletAddress;
      record.targetAddress = params.filterAddress;
    } else {
      record.sourceAddress = params.filterAddress;
      record.targetAddress = walletAddress;
    }
    rawQueryData.push(record);

    // add nodes for wallets from top 10 results and interested wallets
    if (
      i < TOP_WALLETS_PER_QUERY ||
      interestedAddresses
        .filter((ia) => params.chain === ia.chain)
        .includes(extractAddress(walletAddress))
    ) {
      if (addressMap.has(extractAddress(walletAddress)) === false) {
        const newNode = newGenericNode({
          address: walletAddress,
          interested: interestedAddresses
            .filter((ia) => params.chain === ia.chain)
            .includes(extractAddress(walletAddress)),
          chain: params.chain,
        });

        newNode.position = {
          x: isInflow
            ? focusNode.position.x - nodeXSeparation
            : focusNode.position.x + nodeXSeparation,
          y: isInflow
            ? focusNode.position.y + leftYPointer
            : focusNode.position.y + rightYPointer,
        };

        if (isInflow) {
          leftYPointer += nodeYSeparation;
        } else {
          rightYPointer += nodeYSeparation;
        }

        nodes.push(newNode);
        addressMap.set(extractAddress(walletAddress), newNode.id);
      }
    }
  }
};

/**
 * Adds a set of nodes to a group. This is triggered post group modal form submission.
 * @param {Object} nodeComputeParams
 * @param {Array} nodeComputeParams.nodes - list of nods in tracer state
 * @param {Map<string,string>} nodeComputeParams.addressMap - The nodeIds to add to the group
 * @param {Object[]} nodeComputeParams.rawQueryData - the chain of the address being searched
 * @param {{txnTime:number,tokenFlow:Object[]}} nodeComputeParams.results - the address being searched
 * @param {Object} nodeComputeParams.params
 */
export const computeNodeStateWithNewTransaction = ({
  nodes,
  rawQueryData,
  addressMap,
  results,
  params,
}) => {
  const { txnTime, tokenFlow } = results;
  for (let i = 0; i < tokenFlow.length; i++) {
    const { flow, sourceAddress, targetAddress, tokenAddress } = tokenFlow[i];

    // merge results and search params into queryData
    const record = {
      amount: flow,
      sourceAddress,
      targetAddress,
      tokenAddress,
      txnTime,
      ...params,
    };

    rawQueryData.push(record);
    prepareFocusNode({
      addressMap,
      filterAddress: sourceAddress,
      chain: params.chain,
      nodes,
      searchParams: [{ ...params, txnTime }],
    });

    prepareFocusNode({
      addressMap,
      filterAddress: targetAddress,
      chain: params.chain,
      nodes,
      searchParams: [{ ...params, txnTime }],
    });
  }
};

/**
 * adding query data to generic edge
 * @param {Object} existingEdge
 * @param {String} queryId
 * @param {Object} queryData
 */
const _addQueryToGenericEdge = (existingEdge, queryId, queryData) => {
  existingEdge.data.queries[queryId] = queryData;
};

/**
 * Handle existing group edge, adding query data to member edge
 * @param {Object} existingEdge
 * @param {String} queryId
 * @param {Object} queryData
 */
const _addQueryToGroupEdge = (existingEdge, queryId, queryData) => {
  const { sourceAddress, targetAddress, chain } = queryData;

  const sourceAddressId = addressNodeId({ address: sourceAddress, chain });
  const targetAddressId = addressNodeId({ address: targetAddress, chain });
  const memberEdge = existingEdge.data.members.find((member) => {
    return (
      member.id ===
      tokenFlowEdgeId({
        source: sourceAddressId,
        target: targetAddressId,
      })
    );
  });

  if (memberEdge) {
    _addQueryToGenericEdge(memberEdge, queryId, queryData);
  } else {
    existingEdge.data.members.push(
      newGenericEdge({
        source: sourceAddressId,
        target: targetAddressId,

        queries: { [queryId]: queryData },
      })
    );
  }
};

/**
 * Adds a set of nodes to a group. This is triggered post group modal form submission.
 * @param {Object} edgeComputeParams
 * @param {Map<string,string>} edgeComputeParams.addressMap - The nodeIds to add to the group
 * @param {import('./tracerStore').RawQueryRow[]} edgeComputeParams.rawQueryData - the chain of the address being searched
 * @param {import('./tracerStore').TracerEdge[]} edgeComputeParams.edges - list of edges in tracer state

 */
export const computeEdgeStateWithNewTokenFlow = ({
  edges,
  rawQueryData,

  addressMap,
}) => {
  for (let i = 0; i < rawQueryData.length; i++) {
    const {
      sourceAddress,
      targetAddress,
      startTime,
      endTime,
      chain,
      filterAddress,
      tokenAddress,
      transactionHash,
    } = rawQueryData[i];

    // check if both source and target addresses are in the graph
    // source and target are either groupNodeId | addressNodeId
    if (
      addressMap.has(extractAddress(sourceAddress)) &&
      addressMap.has(extractAddress(targetAddress))
    ) {
      const source = addressMap.get(extractAddress(sourceAddress));
      const target = addressMap.get(extractAddress(targetAddress));
      const queryId = newQueryId({
        filterAddress,
        tokenAddress,
        start: startTime,
        end: endTime,
        chain,
        transactionHash,
      });

      const existingEdge = edges.find(
        (edge) => edge.id === tokenFlowEdgeId({ source, target })
      );

      if (existingEdge) {
        // if the edge is a group edge, find the member that has the query and add it to the member
        if (existingEdge.type === EDGE_TYPE.GROUP) {
          _addQueryToGroupEdge(existingEdge, queryId, rawQueryData[i]);
        }
        // if the edge is a generic edge, add the query to the edge
        else if (existingEdge.type === EDGE_TYPE.GENERIC) {
          _addQueryToGenericEdge(existingEdge, queryId, rawQueryData[i]);
        }
      } else {
        // if no temp edge found, create a new edge and add it to the graph
        // If either are group nodes, create a group edge
        if (source.includes('group') || target.includes('group')) {
          edges.push(
            newGroupEdge({
              source,
              target,
              members: [
                newGenericEdge({
                  source,
                  target,

                  queries: { [queryId]: rawQueryData[i] },
                }),
              ],
            })
          );
        } else {
          edges.push(
            newGenericEdge({
              source,
              target,

              queries: { [queryId]: rawQueryData[i] },
            })
          );
        }
      }
    }
  }
};

/**
 * remove dupicate queries that don't have the same key
 * @param {Object} queries
 * @param {String} queries.tokenAddress - the address being searched
 * @param {String} queries.sourceAddress - the chain of the address being searched
 * @param {String} queries.startTime - the address being searched
 * @param {String} queries.endTime - the chain of the address being searched
 * @param {String} queries.targetAddress - the address being searched
 * @param {String} queryKey
 */
const _removeDuplicateQueries = (queries, queryKey) => {
  for (const key in queries) {
    if (
      queries[queryKey] &&
      key !== queryKey &&
      extractAddress(queries[queryKey].tokenAddress) ===
        extractAddress(queries[key].tokenAddress) &&
      extractAddress(queries[queryKey].sourceAddress) ===
        extractAddress(queries[key].sourceAddress) &&
      extractAddress(queries[queryKey].targetAddress) ===
        extractAddress(queries[key].targetAddress) &&
      ((queries[queryKey].startTime &&
        queries[queryKey].startTime === queries[key].startTime &&
        queries[queryKey].endTime &&
        queries[queryKey].endTime === queries[key].endTime) ||
        (queries[queryKey].transactionHash &&
          queries[queryKey].transactionHash === queries[key].transactionHash))
    ) {
      delete queries[key];
    }
  }
};

/**
 * removes a query from an edge. This is triggered on clicking delete on a edge detail row.
 * @param {Object} removeQueryFromEdgeParams
 * @param {Record<string,Map<string,string>>} removeQueryFromEdgeParams.addressMap - The nodeIds to add to the group
 * @param {String} removeQueryFromEdgeParams.edgeId - edge being removed from
 * @param {String} removeQueryFromEdgeParams.queryKey - query to remove
 * @param {Object[]} removeQueryFromEdgeParams.edges - list of edges in tracer state
 * @param {Object[]} removeQueryFromEdgeParams.nodes - list of nodes in tracer state
 * @param {Boolean} removeQueryFromEdgeParams.removeFloatingNodes
 */
export const removeQueryFromEdge = ({
  edgeId,
  queryKey,
  edges,
  nodes,
  addressMap,
  removeFloatingNodes,
}) => {
  const newEdges = [];
  const newNodes = [];
  const nodesToKeep = new Set();
  const newAddressMap = {};

  for (const chain in addressMap) {
    newAddressMap[chain] = new Map(addressMap[chain]);
  }

  const addEdge = (edge) => {
    newEdges.push(edge);
    nodesToKeep.add(edge.source);
    nodesToKeep.add(edge.target);
  };

  for (let i = 0; i < edges.length; i++) {
    const edge = { ...edges[i] };

    if (edge.id === edgeId) {
      if (edge.type === EDGE_TYPE.GROUP) {
        const memberEdge = edge.data.members.find(
          (member) => queryKey in member.data.queries
        );

        delete memberEdge.data.queries[queryKey];

        if (Object.keys(memberEdge.data.queries).length === 0) {
          edge.data.members = edge.data.members.filter(
            (member) => member.id !== memberEdge.id
          );
        }

        if (edge.data.members.length > 0) {
          addEdge(edge);
        }
      } else if (edge.type === EDGE_TYPE.GENERIC) {
        _removeDuplicateQueries(edge.data.queries, queryKey);
        delete edge.data.queries[queryKey];

        if (Object.keys(edge.data.queries).length > 0) {
          addEdge(edge);
        }
      }
    } else {
      addEdge(edge);
    }
  }

  for (const node of nodes) {
    if (nodesToKeep.has(node.id) || !removeFloatingNodes) {
      newNodes.push(node);
    }
    if (removeFloatingNodes && !nodesToKeep.has(node.id)) {
      newAddressMap[node.data.chain].delete(extractAddress(node.data.address));
    }
  }

  return { newNodes, newEdges, newAddressMap };
};

/**
 * Adds a set of nodes to a group. This is triggered post group modal form submission.
 * @param {Object} removeQueryFromEdgeParams
 * @param {Record<string,Map<string,string>>} removeQueryFromEdgeParams.addressMap - The nodeIds to add to the group
 * @param {Object[]} removeQueryFromEdgeParams.nodes - list of nodes in tracer state
 * @param {Object[]} removeQueryFromEdgeParams.edges - list of edges in tracer state
 * @param {Boolean} removeQueryFromEdgeParams.removeFloatingNodes
 * @param {String} removeQueryFromEdgeParams.queryKey
 * @param {String} removeQueryFromEdgeParams.nodeId
 */
export const removeQueryFromNode = ({
  nodeId,
  queryKey,
  edges,
  nodes,
  addressMap,
  removeFloatingNodes,
}) => {
  const newEdges = [];
  const newNodes = [];
  const nodesToKeep = new Set();
  const newAddressMap = {};

  for (const chain in addressMap) {
    newAddressMap[chain] = new Map(addressMap[chain]);
  }

  const addEdge = (edge) => {
    newEdges.push(edge);
    nodesToKeep.add(edge.source);
    nodesToKeep.add(edge.target);
  };

  for (let i = 0; i < edges.length; i++) {
    const edge = { ...edges[i] };

    if (edge.type === EDGE_TYPE.GROUP) {
      const memberEdge = edge.data.members.find(
        (member) => queryKey in member.data.queries
      );

      delete memberEdge.data.queries[queryKey];

      if (Object.keys(memberEdge.data.queries).length === 0) {
        edge.data.members = edge.data.members.filter(
          (member) => member.id !== memberEdge.id
        );
      }

      if (edge.data.members.length > 0) {
        addEdge(edge);
      }
    } else if (edge.type === EDGE_TYPE.GENERIC) {
      _removeDuplicateQueries(edge.data.queries, queryKey);
      delete edge.data.queries[queryKey];

      if (Object.keys(edge.data.queries).length > 0) {
        addEdge(edge);
      }
    }
  }
  let newSelectedNodeData;
  for (const node of nodes) {
    const newNode = { ...node };
    if (newNode.id === nodeId) {
      newNode.data.searchParams = newNode.data.searchParams.filter(
        (q) =>
          newQueryId({
            start: q.startTime,
            end: q.endTime,
            filterAddress: q.filterAddress,
            chain: q.chain,
            tokenAddress: q.tokenAddress,
          }) !== queryKey
      );

      newSelectedNodeData = newNode.data;
    }

    if (nodesToKeep.has(newNode.id) || !removeFloatingNodes) {
      newNodes.push(newNode);
    }
    if (removeFloatingNodes && !nodesToKeep.has(newNode.id)) {
      newAddressMap[newNode.data.chain].delete(
        extractAddress(newNode.data.address)
      );
    }
  }

  return { newNodes, newEdges, newAddressMap, newSelectedNodeData };
};
